
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Collapse,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch
} from "antd";
import { useEffect } from "react";

import CodeImplementationField from "src/components/common-utils/CodeImplementationField";

const { Option } = Select;
const { Panel } = Collapse;
const { TextArea } = Input;

const PROBLEM_DIFFICURTIES = ["EASY", "MEDIUM", "HARD"];
const SOURCE_PLATFORMS = [
  "CODEFORCES_PROBLEM",
  "OTHER_WITH_LINK",
  "OTHER",
  "CODECHEF_PROBLEM",
  "ATCODER_PROBLEM",
  "CSES_PROBLEM",
  "GFG_PROBLEM",
  "LEETCODE_PROBLEM",
  "HACKEREARTH_PROBLEM",
  "HACKERRANK_PROBLEM",
  "SPOJ_PROBLEM"
];

const CreateEditProblemModal = ({
  open,
  onCancel = () => {},
  onCreate = () => {},
  loading = false,
  initialValues = {},
  isEdit = false
}) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (open) {
      form.resetFields();
      if (initialValues && Object.keys(initialValues).length > 0) {
        form.setFieldsValue(initialValues);
      } else {
        form.setFieldsValue({
          isVisible: true,
          isPreviewAvailable: false,
          problemSetDisplayOrder: 1,
          editorial: {
            hints: [],
            codeSnippets: []
          }
        });
      }
    }
  }, [open, initialValues, form]);

  const handleOk = () => {
    form
      .validateFields()
      .then((values) => {
        onCreate(values);
      })
      .catch(() => {
        // Validation failed
      });
  };

  return (
    <Modal
      open={open}
      title={isEdit ? "Edit Problem" : "Create Problem"}
      okText={isEdit ? "Update" : "Create"}
      cancelText="Cancel"
      onCancel={onCancel}
      onOk={handleOk}
      confirmLoading={loading}
      width={800}
      maskClosable={false}
    >
      <Form form={form} layout="vertical" name="create_edit_problem_form">
        <Form.Item
          name="title"
          label="Title"
          rules={[{ required: true, message: "Please input the title!" }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="link"
          label="Problem Link"
          rules={[
            { required: true, message: "Please input the link!" },
            { type: "url", message: "Please enter a valid URL!" }
          ]}
        >
          <Input />
        </Form.Item>

        <Space style={{ display: "flex", flexWrap: "wrap" }} align="start">
          <Form.Item
            name="difficulty"
            label="Difficulty"
            style={{ width: "min(200px, 100%)" }}
            rules={[{ required: true, message: "Please select difficulty!" }]}
          >
            <Select placeholder="Select Difficulty">
              {PROBLEM_DIFFICURTIES.map((diff) => (
                <Option key={diff} value={diff}>
                  {diff}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="sourcePlatform"
            label="Source Platform"
            style={{ width: "min(250px, 100%)" }}
            rules={[{ required: true, message: "Please select platform!" }]}
          >
            <Select placeholder="Select Platform">
              {SOURCE_PLATFORMS.map((platform) => (
                <Option key={platform} value={platform}>
                  {platform}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Space>

        <Space wrap>
          <Form.Item
            name="problemSetDisplayOrder"
            label="Display Order"
            rules={[{ required: true, message: "Please input the display order!" }]}
          >
            <InputNumber min={1} />
          </Form.Item>

          <Form.Item name="isVisible" label="Visible" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item name="isPreviewAvailable" label="Preview" valuePropName="checked" tooltip="When ON, non-enrolled users can see the title and solve the problem for free.">
            <Switch />
          </Form.Item>
        </Space>

        <Form.Item name="tags" label="Tags">
          <Select mode="tags" placeholder="Add tags" tokenSeparators={[","]} />
        </Form.Item>

        <Collapse defaultActiveKey={["editorial"]} style={{ marginBottom: 16 }}>
          <Panel header="Editorial" key="editorial">
            <Form.Item name={["editorial", "videoSolutionUrl"]} label="Video Solution URL">
              <Input placeholder="https://..." />
            </Form.Item>
            <Form.Item name={["editorial", "theoryMarkdown"]} label="Theory (Markdown)">
              <TextArea rows={4} />
            </Form.Item>
            <Form.List name={["editorial", "codeSnippets"]}>
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Collapse key={key} style={{ marginBottom: 16 }}>
                      <Panel
                        forceRender
                        header={`Snippet ${extractTitle(form, name) || key + 1}`}
                        extra={
                          <MinusCircleOutlined
                            onClick={(e) => {
                              e.stopPropagation();
                              remove(name);
                            }}
                          />
                        }
                      >
                        <Form.Item
                          {...restField}
                          name={[name, "snippetTitle"]}
                          label="Snippet Title (Optional)"
                        >
                          <Input />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, "displayOrder"]}
                          label="Order"
                          rules={[{ required: true, message: "Missing order" }]}
                        >
                          <InputNumber min={1} />
                        </Form.Item>

                        <CodeImplementationField name={[name, "implementations"]} />
                      </Panel>
                    </Collapse>
                  ))}
                  <Form.Item>
                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                      Add Code Snippet
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>

            <Form.Item label="Hints">
              <Form.List name={["editorial", "hints"]}>
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <Card
                        size="small"
                        key={key}
                        style={{ marginBottom: 16 }}
                        extra={<MinusCircleOutlined onClick={() => remove(name)} />}
                      >
                        <Form.Item
                          {...restField}
                          name={[name, "title"]}
                          label="Hint Title"
                          rules={[{ required: true, message: "Missing hint title" }]}
                        >
                          <Select placeholder="Select Hint">
                            {["Hint 1", "Hint 2", "Hint 3", "Hint 4", "Hint 5"].map((hint) => (
                              <Option key={hint} value={hint}>
                                {hint}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, "content"]}
                          label="Hint Content"
                          rules={[{ required: true, message: "Missing hint content" }]}
                        >
                          <TextArea rows={2} />
                        </Form.Item>
                      </Card>
                    ))}
                    <Form.Item>
                      <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                        Add Hint
                      </Button>
                    </Form.Item>
                  </>
                )}
              </Form.List>
            </Form.Item>
          </Panel>
        </Collapse>
      </Form>
    </Modal>
  );
};

// Helper to get title for dynamic panel header
const extractTitle = (form, index) => {
  const snippets = form.getFieldValue(["editorial", "codeSnippets"]) || [];
  return snippets[index]?.snippetTitle;
};

export default CreateEditProblemModal;
